import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from './supabase'
import type { Category } from './tokens'
import { fetchChatModels, type MemoItem, type ChatModelList } from './api'

export function useCategories() {
  return useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ml_categories').select('*').order('sort_order')
      if (error) throw error
      return data ?? []
    },
    staleTime: 1000 * 60 * 5,
  })
}

// 💬チャットで選べるモデルの一覧（Worker の models.ts が正本）。メモ詳細と /chat で共用する。
export function useChatModels() {
  return useQuery<ChatModelList>({
    queryKey: ['chat-models'],
    queryFn: fetchChatModels,
    staleTime: 1000 * 60 * 5,
  })
}

export function useItems(params?: { cat?: string | null; status?: string; limit?: number; favorite?: boolean; since?: string }) {
  return useQuery<MemoItem[]>({
    queryKey: ['items', params],
    queryFn: async () => {
      let query = supabase
        .from('ml_items')
        .select('id,kind,cat_id,status,title,summary,digest,original_content,gemini_note,rewritten_text,custom_prompt,process_mode,confidence,source_url,author,created_at,discord_msg_id,og_image_url,image_url,rating,is_favorite')
        .eq('status', params?.status ?? 'done')
        .order('created_at', { ascending: false })
        .limit(params?.limit ?? 60)
      if (params?.cat) query = query.eq('cat_id', params.cat)
      if (params?.favorite) query = query.eq('is_favorite', true)
      if (params?.since) query = query.gte('created_at', params.since)   // 概要は直近期間だけ取得
      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as MemoItem[]
    },
    staleTime: 1000 * 30,
  })
}

// 検索（Supabase ilike + fts）
export function useSearch(q: string) {
  return useQuery<MemoItem[]>({
    queryKey: ['search', q],
    enabled: q.trim().length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ml_items')
        .select('id,kind,cat_id,status,title,summary,original_content,process_mode,confidence,source_url,author,created_at')
        .eq('status', 'done')
        .or(`title.ilike.%${q}%,summary.ilike.%${q}%,original_content.ilike.%${q}%`)
        .order('created_at', { ascending: false })
        .limit(40)
      if (error) throw error
      return (data ?? []) as MemoItem[]
    },
    staleTime: 1000 * 30,
  })
}

export function useItem(id: string) {
  return useQuery<MemoItem>({
    queryKey: ['item', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('ml_items').select('*').eq('id', id).single()
      if (error) throw error
      return data as MemoItem
    },
  })
}

export function useCategoryCounts() {
  return useQuery<Record<string, number>>({
    queryKey: ['category-counts'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ml_items').select('cat_id').eq('status', 'done')
      if (error) throw error
      const counts: Record<string, number> = {}
      for (const row of data ?? []) {
        const k = row.cat_id ?? 'memo'
        counts[k] = (counts[k] ?? 0) + 1
      }
      return counts
    },
    staleTime: 1000 * 60,
  })
}

// Pending/processing アイテム（Ingest画面用）
export function usePendingItems() {
  return useQuery<MemoItem[]>({
    queryKey: ['pending-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ml_items')
        .select('*')
        .in('status', ['pending', 'processing'])
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as MemoItem[]
    },
    refetchInterval: 5000, // 5秒ごとに自動更新
  })
}

// アーカイブ・ゴミ箱
export function useArchivedItems() {
  return useQuery<MemoItem[]>({
    queryKey: ['archived-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ml_items').select('*').eq('status', 'archived')
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as MemoItem[]
    },
  })
}

export function useTrashedItems() {
  return useQuery<MemoItem[]>({
    queryKey: ['trashed-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ml_items').select('*').eq('status', 'trashed')
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as MemoItem[]
    },
  })
}

// アイテムのステータス変更（アーカイブ・削除・復元）
export function useUpdateItemStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('ml_items').update({ status }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.invalidateQueries({ queryKey: ['pending-items'] })
      qc.invalidateQueries({ queryKey: ['archived-items'] })
      qc.invalidateQueries({ queryKey: ['trashed-items'] })
      qc.invalidateQueries({ queryKey: ['category-counts'] })
    },
  })
}

// メモを Gemini なしで即時保存（Webから新規作成・raw モード相当）
export function useCreateMemo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ content }: { content: string }) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')
      const title = content.split('\n')[0].slice(0, 80).trim() || '無題のメモ'
      const hasUrl = /https?:\/\/\S+/.test(content)
      const kind = hasUrl ? 'link' : 'note'
      const source_url = hasUrl ? content.match(/https?:\/\/\S+/)?.[0] : undefined
      const { error } = await supabase.from('ml_items').insert({
        user_id: session.user.id,
        kind,
        status: 'done',
        process_mode: 'raw',
        cat_id: 'memo',
        original_content: content,
        title,
        ...(source_url && { source_url }),
      })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.invalidateQueries({ queryKey: ['category-counts'] })
    },
  })
}

// アイテム内容の編集（タイトル・原文など）
export function useUpdateItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<MemoItem> }) => {
      const { error } = await supabase.from('ml_items').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['item', vars.id] })
      qc.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

// アイテム内 Gemini 会話の読み込み（時系列）
export interface ItemChatRow {
  id: string
  item_id: string
  role: 'user' | 'assistant'
  content: string
  searched: boolean
  model: string | null
  created_at: string
}

export function useItemChats(itemId: string) {
  return useQuery<ItemChatRow[]>({
    queryKey: ['item-chats', itemId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ml_item_chats')
        .select('*')
        .eq('item_id', itemId)
        .order('created_at', { ascending: true })
      if (error) throw error
      return (data ?? []) as ItemChatRow[]
    },
  })
}

// 「💬 チャット」用：kind='chat' のメモ（=会話スレッド）一覧
export function useChatMemos() {
  return useQuery<MemoItem[]>({
    queryKey: ['chat-memos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ml_items')
        .select('*')
        .eq('kind', 'chat')
        .neq('status', 'trashed')
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as MemoItem[]
    },
  })
}

// 全体チャットのスレッド一覧（Ask the Lake）
export interface ChatSession {
  id: string
  user_id: string
  title: string | null
  created_at: string
  updated_at: string
}

export function useChatSessions() {
  return useQuery<ChatSession[]>({
    queryKey: ['chat-sessions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ml_chat_sessions')
        .select('*')
        .order('updated_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as ChatSession[]
    },
  })
}

// 複数アイテムをまとめてゴミ箱へ（未確認キューの削除など）
export function useTrashItems() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (ids.length === 0) return
      const { error } = await supabase.from('ml_items').update({ status: 'trashed' }).in('id', ids)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending-items'] })
      qc.invalidateQueries({ queryKey: ['items'] })
      qc.invalidateQueries({ queryKey: ['trashed-items'] })
      qc.invalidateQueries({ queryKey: ['category-counts'] })
    },
  })
}

// ── ニュース配信設定 ──────────────────────────────────────
export interface NewsSettings {
  enabled: boolean
  count: number
  collect_per_run: number
  delivery_hour: number
  weekdays: number[]
  exploration: number
  diversity: number
  date_filter_days: number
  exclude_categories: string[]
  include_categories: string[]
  retention_days: number
  remind_initial_days: number
  remind_repeat_days: number
  remind_max: number
}

export const NEWS_SETTINGS_DEFAULTS: NewsSettings = {
  enabled: true, count: 5, collect_per_run: 8, delivery_hour: 7, weekdays: [0, 1, 2, 3, 4, 5, 6],
  exploration: 0.3, diversity: 0.7, date_filter_days: 0,
  exclude_categories: [], include_categories: [],
  retention_days: 14, remind_initial_days: 3, remind_repeat_days: 5, remind_max: 2,
}

export function useNewsSettings() {
  return useQuery<NewsSettings>({
    queryKey: ['news-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ml_news_settings').select('settings').maybeSingle()
      if (error) throw error
      return { ...NEWS_SETTINGS_DEFAULTS, ...((data?.settings ?? {}) as Partial<NewsSettings>) }
    },
  })
}

export function useUpdateNewsSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (settings: NewsSettings) => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      const { error } = await supabase.from('ml_news_settings')
        .upsert({ user_id: user.id, settings, updated_at: new Date().toISOString() }, { onConflict: 'user_id' })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['news-settings'] }),
  })
}

export function useNewsProfile() {
  return useQuery<{ profile_json: any; updated_at: string } | null>({
    queryKey: ['news-profile'],
    queryFn: async () => {
      const { data } = await supabase.from('ml_interest_profile').select('profile_json,updated_at').maybeSingle()
      return (data as any) ?? null
    },
  })
}

export interface NewsArticleRow {
  id: string; title: string | null; url: string; summary: string | null; cat_id: string | null
  rating: number | null; is_favorite: boolean | null; read_later: boolean | null; is_detailed: boolean | null
  item_id: string | null; posted_at: string | null; created_at: string
}
export function useNewsArticles(limit = 100) {
  return useQuery<NewsArticleRow[]>({
    queryKey: ['news-articles', limit],
    queryFn: async () => {
      const { data, error } = await supabase.from('ml_news_articles')
        .select('id,title,url,summary,cat_id,rating,is_favorite,read_later,is_detailed,item_id,posted_at,created_at')
        .order('created_at', { ascending: false }).limit(limit)
      if (error) throw error
      return (data ?? []) as NewsArticleRow[]
    },
  })
}

export function useBanditArms() {
  return useQuery<{ cat_id: string; pulls: number; rewards: number }[]>({
    queryKey: ['bandit-arms'],
    queryFn: async () => {
      const { data } = await supabase.from('ml_bandit_arms').select('cat_id,pulls,rewards').order('pulls', { ascending: false })
      return (data ?? []) as any
    },
  })
}

// カテゴリ別タグ
export function useTags() {
  return useQuery<{ id: string; name: string }[]>({
    queryKey: ['tags'],
    queryFn: async () => {
      const { data, error } = await supabase.from('ml_tags').select('id,name').order('name')
      if (error) throw error
      return data ?? []
    },
    staleTime: 1000 * 60 * 5,
  })
}
